import { useEffect, useState } from 'react';
import { useFeedback } from '../common/Feedback';
import { ShieldCheck, Plus, Trash2, Save, Wifi, UserRound } from 'lucide-react';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { useMembers } from '../../hooks/useMembers';
import { formatarNome } from '../../utils/nomes';
import {
  DIAS_DA_SEMANA,
  FAIXA_PADRAO,
  FUSO_PADRAO,
  FaixaDeHorario,
  PoliticaDeAcesso,
  descreverHorario,
  ipForaDasFaixas,
  listaParaTexto,
  mensagemDeErroDaPolitica,
  previaDaPolitica,
  textoDaConfirmacaoDeIp,
  textoParaLista,
} from '../../utils/accessPolicy';

// Etapa 18: regras de acesso (horário, IP) e sites bloqueados. Só owner/admin (a página de
// Configurações decide); as regras valem só para members.

const estiloDoCartao = { padding: 'var(--space-6)', borderRadius: 'var(--radius-lg)', display: 'flex', flexDirection: 'column' as const, gridColumn: '1 / -1' };
const estiloDoBotao = { display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', fontWeight: 'var(--font-medium)', fontSize: 'var(--text-sm)', cursor: 'pointer' } as const;
const estiloDoCampo = { background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' } as const;
const estiloDoRotulo = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' } as const;
const estiloDaAjuda = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' } as const;
const estiloDaSecao = { display: 'flex', flexDirection: 'column' as const, gap: 'var(--space-2)' };

function caixaDeAviso(tipo: 'success' | 'error' | 'warning') {
  const fundo = tipo === 'success' ? 'rgba(34, 197, 94, 0.1)' : tipo === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)';
  const cor = tipo === 'success' ? 'var(--color-success)' : tipo === 'error' ? 'var(--color-error)' : 'var(--color-text-primary)';
  return { padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)', background: fundo, color: cor };
}

const novaFaixa = (): FaixaDeHorario => ({ ...FAIXA_PADRAO, dias: [...FAIXA_PADRAO.dias] });

/** Lista de faixas (dias + das/às), usada no horário geral e no horário de cada membro. */
function EditorDeHorarios({ faixas, aoMudar, vazio }: { faixas: FaixaDeHorario[]; aoMudar: (faixas: FaixaDeHorario[]) => void; vazio: string }) {
  const mudarFaixa = (indice: number, mudanca: Partial<FaixaDeHorario>) =>
    aoMudar(faixas.map((faixa, i) => (i === indice ? { ...faixa, ...mudanca } : faixa)));

  const alternarDia = (indice: number, dia: number) => {
    const dias = faixas[indice].dias;
    mudarFaixa(indice, { dias: dias.includes(dia) ? dias.filter((d) => d !== dia) : [...dias, dia].sort((a, b) => a - b) });
  };

  return (
    <>
      {faixas.length === 0 && <p style={estiloDaAjuda}>{vazio}</p>}
      {faixas.map((faixa, indice) => (
        <div key={indice} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {DIAS_DA_SEMANA.map((dia) => (
              <label key={dia.numero} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
                <input type="checkbox" checked={faixa.dias.includes(dia.numero)} onChange={() => alternarDia(indice, dia.numero)} />
                {dia.curto}
              </label>
            ))}
          </div>
          <label style={estiloDoRotulo}>
            das <input type="time" value={faixa.inicio} onChange={(e) => mudarFaixa(indice, { inicio: e.target.value })} style={estiloDoCampo} />
          </label>
          <label style={estiloDoRotulo}>
            às <input type="time" value={faixa.fim} onChange={(e) => mudarFaixa(indice, { fim: e.target.value })} style={estiloDoCampo} />
          </label>
          <button type="button" onClick={() => aoMudar(faixas.filter((_, i) => i !== indice))} style={{ ...estiloDoBotao, color: 'var(--color-error)' }}>
            <Trash2 size={14} /> Remover
          </button>
        </div>
      ))}
      <div>
        <button type="button" onClick={() => aoMudar([...faixas, novaFaixa()])} style={estiloDoBotao}>
          <Plus size={14} /> Adicionar horário
        </button>
      </div>
    </>
  );
}

export function AccessPolicySection() {
  const { confirmar } = useFeedback();
  const { policy, isLoading, error, fetchPolicy, save, testIp } = useAccessPolicy();
  const [faixas, setFaixas] = useState<FaixaDeHorario[]>([]);
  // Horário próprio de alguns membros (id → faixas; vazio = sem limite para aquele membro).
  const [horariosPorMembro, setHorariosPorMembro] = useState<Record<string, FaixaDeHorario[]>>({});
  const { members, fetchMembers } = useMembers();
  const [textoDasRedes, setTextoDasRedes] = useState('');
  const [textoDosSites, setTextoDosSites] = useState('');
  const [fuso, setFuso] = useState(FUSO_PADRAO);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [testeDoIp, setTesteDoIp] = useState<{ type: 'success' | 'error' | 'warning'; msg: string } | null>(null);

  useEffect(() => {
    fetchPolicy();
    fetchMembers();
  }, [fetchPolicy, fetchMembers]);

  // O formulário passa a mostrar o que está salvo (já normalizado pelo servidor).
  useEffect(() => {
    if (!policy) return;
    setFaixas(policy.allowed_hours);
    setHorariosPorMembro(policy.member_hours ?? {});
    setTextoDasRedes(listaParaTexto(policy.allowed_cidrs));
    setTextoDosSites(listaParaTexto(policy.blocked_domains));
    setFuso(policy.timezone || FUSO_PADRAO);
  }, [policy]);

  const politicaDoFormulario: PoliticaDeAcesso = {
    allowed_hours: faixas,
    member_hours: horariosPorMembro,
    timezone: fuso,
    allowed_cidrs: textoParaLista(textoDasRedes),
    blocked_domains: textoParaLista(textoDosSites),
  };

  // Só quem tem o papel membro (proprietários e administradores nunca são bloqueados) e já entrou.
  const membrosElegiveis = members.filter((m) => m.role === 'member' && !m.is_pending);
  const nomeDoMembro = (id: string) => {
    const m = members.find((x) => x.id === id);
    return m ? formatarNome(m.name) || m.email : 'Membro';
  };
  const emailDoMembro = (id: string) => members.find((x) => x.id === id)?.email ?? '';
  const semHorarioProprio = membrosElegiveis.filter((m) => !(m.id in horariosPorMembro));
  const comHorarioProprio = Object.keys(horariosPorMembro).sort((a, b) => nomeDoMembro(a).localeCompare(nomeDoMembro(b), 'pt-BR'));

  const adicionarMembro = (id: string) => {
    if (!id) return;
    // Começa com uma cópia do horário geral (ou o horário comercial, se não houver geral).
    const inicial = faixas.length > 0 ? faixas.map((f) => ({ ...f, dias: [...f.dias] })) : [novaFaixa()];
    setHorariosPorMembro((atuais) => ({ ...atuais, [id]: inicial }));
  };
  const mudarHorarioDoMembro = (id: string, novas: FaixaDeHorario[]) =>
    setHorariosPorMembro((atuais) => ({ ...atuais, [id]: novas }));
  const tirarMembro = (id: string) =>
    setHorariosPorMembro((atuais) => {
      const proximos = { ...atuais };
      delete proximos[id];
      return proximos;
    });

  const handleTestIp = async () => {
    setTesteDoIp(null);
    try {
      const { ip, allowed } = await testIp(politicaDoFormulario.allowed_cidrs);
      const seuIp = `Seu IP atual: ${ip || 'não identificado'}.`;
      if (politicaDoFormulario.allowed_cidrs.length === 0) {
        setTesteDoIp({ type: 'success', msg: `${seuIp} Sem faixas de IP, qualquer rede é aceita.` });
      } else if (allowed) {
        setTesteDoIp({ type: 'success', msg: `${seuIp} Ele está dentro das faixas informadas.` });
      } else {
        setTesteDoIp({ type: 'warning', msg: `${seuIp} Ele está fora das faixas informadas: membros nesta rede seriam bloqueados.` });
      }
    } catch (err) {
      setTesteDoIp({ type: 'error', msg: mensagemDeErroDaPolitica(err) });
    }
  };

  const handleSave = async () => {
    setStatus(null);
    if ([faixas, ...Object.values(horariosPorMembro)].some((lista) => lista.some((faixa) => faixa.dias.length === 0))) {
      setStatus({ type: 'error', msg: 'Marque pelo menos um dia em cada horário.' });
      return;
    }
    setIsSaving(true);
    try {
      try {
        await save(politicaDoFormulario, false);
      } catch (err) {
        // Item 6 da etapa: regra de IP que deixa de fora o IP de quem salva pede confirmação extra.
        const ip = ipForaDasFaixas(err);
        if (!ip) throw err;
        if (!(await confirmar({ titulo: 'Salvar mesmo assim?', mensagem: textoDaConfirmacaoDeIp(ip), confirmar: 'Salvar mesmo assim' }))) return;
        await save(politicaDoFormulario, true);
      }
      setStatus({ type: 'success', msg: 'Regras de acesso salvas!' });
    } catch (err) {
      setStatus({ type: 'error', msg: mensagemDeErroDaPolitica(err) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="glass" style={estiloDoCartao}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
        <div style={{ background: 'var(--color-bg-elevated)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)' }}>
          <ShieldCheck size={24} color="var(--color-primary-400)" />
        </div>
        <div>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)' }}>Regras de acesso</h3>
          <p style={estiloDaAjuda}>
            Quando e de onde os membros podem usar as credenciais, e em quais sites a extensão não oferece nada.
            Proprietários e administradores nunca são bloqueados.
          </p>
        </div>
      </div>

      {error && <div style={{ ...caixaDeAviso('error'), marginBottom: 'var(--space-4)' }}>{error}</div>}
      {isLoading && !policy && <p style={estiloDaAjuda}>Carregando...</p>}

      {policy && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Horário geral: vale para todos os membros que não têm horário próprio */}
          <div style={estiloDaSecao}>
            <label style={estiloDoRotulo}>Horário para todos os membros</label>
            <EditorDeHorarios
              faixas={faixas}
              aoMudar={setFaixas}
              vazio="Em aberto: os membros podem usar as credenciais a qualquer dia e hora."
            />
            <p style={estiloDaAjuda}>Para atravessar a meia-noite, crie dois horários (ex.: 22:00 às 23:59 e 00:00 às 06:00).</p>
          </div>

          {/* Horário por membro: substitui o geral só para aquela pessoa */}
          <div style={estiloDaSecao}>
            <label style={estiloDoRotulo}>Horário por membro</label>
            <p style={estiloDaAjuda}>
              Para quem precisa de um horário diferente. O horário próprio substitui o horário para todos só para essa pessoa;
              a rede e os sites bloqueados continuam valendo para ela.
            </p>

            {comHorarioProprio.map((id) => {
              const proprias = horariosPorMembro[id];
              const emAberto = proprias.length === 0;
              return (
                <div key={id} style={{ border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', background: 'var(--color-bg-root)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                      <UserRound size={16} style={{ color: 'var(--color-primary-400)', flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', overflowWrap: 'anywhere' }}>{nomeDoMembro(id)}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>{emailDoMembro(id)}</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => tirarMembro(id)} style={estiloDoBotao}>
                      Voltar ao horário geral
                    </button>
                  </div>
                  <div role="radiogroup" aria-label={`Horário de ${nomeDoMembro(id)}`} style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', fontSize: 'var(--text-sm)' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                      <input type="radio" name={`modo-${id}`} checked={emAberto} onChange={() => mudarHorarioDoMembro(id, [])} />
                      Sem limite de horário
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                      <input type="radio" name={`modo-${id}`} checked={!emAberto} onChange={() => { if (emAberto) mudarHorarioDoMembro(id, [novaFaixa()]); }} />
                      Horários próprios
                    </label>
                  </div>
                  {!emAberto && (
                    <EditorDeHorarios
                      faixas={proprias}
                      aoMudar={(novas) => mudarHorarioDoMembro(id, novas)}
                      vazio="Sem horário próprio: este membro pode usar a qualquer dia e hora."
                    />
                  )}
                  <p style={{ ...estiloDaAjuda, fontSize: 'var(--text-xs)' }}>
                    {nomeDoMembro(id)} poderá usar as credenciais {descreverHorario(proprias)}.
                  </p>
                </div>
              );
            })}

            {semHorarioProprio.length > 0 ? (
              <select
                value=""
                onChange={(e) => adicionarMembro(e.target.value)}
                aria-label="Dar horário próprio a um membro"
                className="filtro-select"
                style={{ maxWidth: 360 }}
              >
                <option value="">+ Dar horário próprio a um membro…</option>
                {semHorarioProprio.map((m) => (
                  <option key={m.id} value={m.id}>{formatarNome(m.name) || m.email}</option>
                ))}
              </select>
            ) : (
              membrosElegiveis.length === 0 && <p style={estiloDaAjuda}>Ainda não há membros nesta organização.</p>
            )}
          </div>

          {/* Faixas de IP */}
          <div style={estiloDaSecao}>
            <label style={estiloDoRotulo}>Faixas de IP permitidas</label>
            <textarea
              value={textoDasRedes}
              onChange={(e) => setTextoDasRedes(e.target.value)}
              rows={3}
              placeholder="200.100.50.0/24"
              style={{ ...estiloDoCampo, fontFamily: 'monospace', resize: 'vertical' }}
            />
            <p style={estiloDaAjuda}>Uma faixa por linha (ex.: 200.100.50.0/24). Vazio = qualquer rede.</p>
            <div>
              <button type="button" onClick={handleTestIp} style={estiloDoBotao}>
                <Wifi size={14} /> Testar com o meu IP atual
              </button>
            </div>
            {testeDoIp && <div style={caixaDeAviso(testeDoIp.type)}>{testeDoIp.msg}</div>}
          </div>

          {/* Sites bloqueados */}
          <div style={estiloDaSecao}>
            <label style={estiloDoRotulo}>Sites bloqueados na extensão</label>
            <textarea
              value={textoDosSites}
              onChange={(e) => setTextoDosSites(e.target.value)}
              rows={3}
              placeholder="exemplo.com.br"
              style={{ ...estiloDoCampo, fontFamily: 'monospace', resize: 'vertical' }}
            />
            <p style={estiloDaAjuda}>Um site por linha (ex.: exemplo.com.br). Os subdomínios também ficam bloqueados.</p>
            <div style={caixaDeAviso('warning')}>
              O bloqueio de sites exige a extensão 1.2.0 ou mais nova. Quem ainda usa uma versão anterior não é afetado por ele.
            </div>
          </div>

          {/* Prévia */}
          <div style={estiloDaSecao}>
            <label style={estiloDoRotulo}>Prévia</label>
            <p style={{ padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)', fontSize: 'var(--text-sm)' }}>
              {previaDaPolitica(politicaDoFormulario)}
            </p>
          </div>

          <div>
            {status && <div style={{ ...caixaDeAviso(status.type), marginBottom: 'var(--space-4)' }}>{status.msg}</div>}
            <button type="button" onClick={handleSave} disabled={isSaving} className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', borderRadius: 'var(--radius-md)', fontWeight: 'var(--font-medium)' }}>
              <Save size={18} /> {isSaving ? 'Salvando...' : 'Salvar regras'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
